import { useSubmit } from "@certn/api";
import {
  BasicButton,
  DangerButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
} from "@certn/cascadia-react";
import { PrimaryButton, showToast, Text } from "@certn/cascadia";
import { useTranslation } from "react-i18next";

interface ReactivateDeactivateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  name: string;
  isActive: boolean;
  mutate: () => void;
  apiPathPrefix: string;
}

export function ReactivateDeactivateModal({
  isOpen,
  onClose,
  templateId,
  name,
  isActive,
  mutate,
  apiPathPrefix,
}: ReactivateDeactivateModalProps) {
  const { t } = useTranslation();

  const endpoint = isActive
    ? `/api/internal/client-portal/check-arguments-templates/${apiPathPrefix}/${templateId}/deactivate/`
    : `/api/internal/client-portal/check-arguments-templates/${apiPathPrefix}/${templateId}/activate/`;

  const { submit, isSubmitting } = useSubmit(endpoint, {
    throwErrors: true,
    showToastOnError: true,
    onSuccess: () => {
      showToast(
        isActive
          ? t("Template deactivated successfully")
          : t("Template activated successfully"),
        { type: "success" }
      );
      mutate();
      onClose();
    },
  });

  return (
    <Modal
      testID={isActive ? "deactivate-modal" : "activate-modal"}
      isOpen={isOpen}
      onOpenChange={onClose}
    >
      <Dialog>
        <DialogHeader onClose={onClose}>
          {isActive ? t("Deactivate template") : t("Activate template")}
        </DialogHeader>
        <DialogContent>
          <Text>
            {isActive
              ? t(
                  'Are you sure you want to deactivate "{{name}}"? It will no longer be available for use in new orders.',
                  { name }
                )
              : t(
                  'Are you sure you want to activate "{{name}}"? It will become available for use in new orders.',
                  { name }
                )}
          </Text>
        </DialogContent>
        <DialogFooter>
          <BasicButton
            onPress={onClose}
            testID="cancel-button"
            isDisabled={isSubmitting}
          >
            {t("Cancel")}
          </BasicButton>
          {isActive ? (
            <DangerButton
              size="medium"
              onPress={() => submit({})}
              testID="confirm-button"
              isDisabled={isSubmitting}
            >
              {t("Deactivate")}
            </DangerButton>
          ) : (
            <PrimaryButton
              onPress={() => submit({})}
              testID="confirm-button"
              isDisabled={isSubmitting}
            >
              {t("Activate")}
            </PrimaryButton>
          )}
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
